import type { ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { SalveazaTargetareListaFisierRequest } from "@/api/types/targetareConfig";
import type {
  TargetareServiciuScurt,
  TargetareTipLista,
} from "@/types/targetare";
import {
  ETICHETE_STARE_ACTUALIZARE,
  ETICHETE_TIP_LISTA,
} from "@/utils/targetareConfig";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import type { RandLista } from "@/utils/targetareRaportListe";
import type { ColoanaTabel } from "@/utils/targetareTabel";
import type { FirmeCriteriuDeschis } from "../FirmeCriteriuDialog";

export type ContextListe = {
  servicii: TargetareServiciuScurt[];
  onSalveaza: (patch: SalveazaTargetareListaFisierRequest) => void;
  onFirme: (deschis: FirmeCriteriuDeschis) => void;
  inCurs: boolean;
};

const opreste = (e: { stopPropagation: () => void }) => e.stopPropagation();
const FARA_SERVICIU = "__fara__";

export function grupeazaServicii(servicii: TargetareServiciuScurt[]) {
  const m = new Map<string, TargetareServiciuScurt[]>();
  servicii.forEach((s) => {
    const k = s.sector ?? "(fără sector)";
    m.set(k, [...(m.get(k) ?? []), s]);
  });
  return Array.from(m.entries());
}

export function SelectServiciu({
  valoare,
  servicii,
  onChange,
  disabled,
  latime = "w-[260px]",
}: {
  valoare: string | null;
  servicii: TargetareServiciuScurt[];
  onChange: (id: string | null) => void;
  disabled?: boolean;
  latime?: string;
}) {
  return (
    <Select
      value={valoare ?? FARA_SERVICIU}
      disabled={disabled}
      onValueChange={(v) => onChange(v === FARA_SERVICIU ? null : v)}
    >
      <SelectTrigger className={`h-7 ${latime} text-xs`}>
        <SelectValue placeholder="Serviciu" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        <SelectItem value={FARA_SERVICIU}>
          Fără serviciu (lista nu contează)
        </SelectItem>
        {grupeazaServicii(servicii).map(([sector, lista]) => (
          <SelectGroup key={sector}>
            <SelectLabel>{sector}</SelectLabel>
            {lista.map((s) => (
              <SelectItem key={s.entity_id} value={s.entity_id}>
                {s.entity_id} · {s.name}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

export function celulaLista(ctx: ContextListe) {
  return (coloana: ColoanaTabel<RandLista>, r: RandLista): ReactNode => {
    const v = r[coloana.id];
    switch (coloana.id) {
      case "nume_fisier":
        return (
          <span className="flex items-center gap-1" title={r.nume_fisier}>
            <span className="truncate">{r.nume_fisier}</span>
            {r.sursa_url && (
              <a
                href={r.sursa_url}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                title={r.sursa_url}
                onClick={opreste}
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </span>
        );
      case "serviciu":
        if (r.service_id === null && r.servicii_randuri.length > 0) {
          return (
            <Badge
              variant="outline"
              className="font-normal"
              title={`Serviciul fiecărui rând, din coloana ${r.coloana_serviciu ?? "importată"}: ${r.servicii_randuri.join(", ")}`}
            >
              pe rând: {r.servicii_randuri.length} servicii
            </Badge>
          );
        }
        return (
          <div onClick={opreste}>
            <SelectServiciu
              valoare={r.service_id}
              servicii={ctx.servicii}
              disabled={ctx.inCurs}
              onChange={(id) =>
                ctx.onSalveaza({
                  nume_fisier: r.nume_fisier,
                  service_id: id ?? "",
                })
              }
            />
          </div>
        );
      case "tip":
        return (
          <div onClick={opreste}>
            <Select
              value={r.tip}
              disabled={ctx.inCurs}
              onValueChange={(t) =>
                ctx.onSalveaza({
                  nume_fisier: r.nume_fisier,
                  tip: t as TargetareTipLista,
                })
              }
            >
              <SelectTrigger className="h-7 w-[120px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(["OFICIALA", "NEOFICIALA"] as TargetareTipLista[]).map(
                  (t) => (
                    <SelectItem key={t} value={t}>
                      {ETICHETE_TIP_LISTA[t]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
        );
      case "activ":
        return (
          <div onClick={opreste}>
            <Switch
              checked={r.activ}
              disabled={ctx.inCurs}
              onCheckedChange={(val) =>
                ctx.onSalveaza({ nume_fisier: r.nume_fisier, activ: val })
              }
            />
          </div>
        );
      case "potrivite":
      case "in_scop":
        return (
          <button
            type="button"
            className="tabular-nums hover:underline disabled:no-underline"
            disabled={r.potrivite === 0}
            title="Deschide firmele de pe listă"
            onClick={(e) => {
              e.stopPropagation();
              ctx.onFirme({
                criteriu: { tip: "lista", nume_fisier: r.nume_fisier },
                titlu: `Firmele de pe lista „${r.nume_fisier}”`,
              });
            }}
          >
            {formatNumar(Number(v ?? 0))}
          </button>
        );
      case "actualizare_automata":
        return (
          <div onClick={opreste}>
            <Switch
              checked={r.actualizare_automata}
              disabled={ctx.inCurs || !r.sursa_url}
              title={
                r.sursa_url
                  ? "Se reia lunar de la sursă"
                  : "Lista nu are adresă sursă"
              }
              onCheckedChange={(val) =>
                ctx.onSalveaza({
                  nume_fisier: r.nume_fisier,
                  actualizare_automata: val,
                })
              }
            />
          </div>
        );
      case "actualizare_stare_text": {
        const stare = r.actualizare_stare;
        if (!stare)
          return <span className="text-xs text-muted-foreground">–</span>;
        const clase =
          stare === "ok"
            ? "border-ds-green-200 bg-ds-green-50 text-ds-green-700"
            : stare === "eroare"
              ? "border-destructive/40 bg-destructive/10 text-destructive"
              : "border-ds-blue-200 bg-ds-blue-50 text-ds-blue-700";
        return (
          <Badge
            variant="outline"
            className={`font-normal ${clase}`}
            title={[
              r.actualizare_la ? formatDataOra(r.actualizare_la) : "",
              r.actualizare_mesaj ?? "",
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            {ETICHETE_STARE_ACTUALIZARE[stare] ?? stare}
          </Badge>
        );
      }
      case "importat_la":
      case "actualizare_la":
      case "actualizat_la":
        return (
          <span className="text-xs">{formatDataOra(v as string | null)}</span>
        );
      case "scadenta":
        return r.scadenta ? (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 font-normal text-amber-800"
          >
            de actualizat
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">la zi</span>
        );
      case "sursa_domeniu":
        return r.sursa_url ? (
          <a
            href={r.sursa_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs hover:underline"
            onClick={opreste}
            title={r.sursa_url}
          >
            {r.sursa_domeniu}
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">–</span>
        );
      default:
        break;
    }
    if (typeof v === "boolean") return v ? "da" : "nu";
    if (typeof v === "number")
      return <span className="tabular-nums">{formatNumar(v)}</span>;
    return (
      <span title={String(v ?? "")} className="block truncate">
        {String(v ?? "")}
      </span>
    );
  };
}
